"use client";

import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface AboutFAQProps {
    faqData?: {
        title?: string;
        subtitle?: string;
        faqs?: Array<{
            id: string;
            question: string;
            answer: string;
            isActive: boolean;
            order: number;
        }>;
    };
}

export default function AboutFAQ({ faqData }: AboutFAQProps) {
    const [activeAccordion, setActiveAccordion] = useState<number | null>(null);

    // Eğer dışarıdan veri gelmediyse veya 'faqs' dizisi boşsa bileşeni gizle
    if (!faqData || !faqData.faqs || faqData.faqs.length === 0) {
        return null;
    }

    // Sadece "Aktif" (isActive: true) olanları filtrele ve "Sıra"ya (order) göre diz
    const activeFaqs = faqData.faqs
        .filter(faq => faq.isActive)
        .sort((a, b) => a.order - b.order);

    // Eğer filtreleme sonucunda hiç aktif soru kalmadıysa bileşeni yine gizle
    if (activeFaqs.length === 0) {
        return null;
    }

    const toggleAccordion = (index: number) => {
        setActiveAccordion(activeAccordion === index ? null : index);
    };

    // Başlığı parçalara ayırma (Örn: "Sıkça Sorulan" siyah, "Sorular" renkli)
    const title = faqData.title || "Sıkça Sorulan Sorular";
    const titleWords = title.split(' ');
    const coloredWord = titleWords.pop() || "Sorular"; // Son kelimeyi al
    const restWords = titleWords.join(' ') || "Sıkça Sorulan"; // Kalanları birleştir

    return (
        <section className="container mx-auto px-4 py-20 bg-white">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        {restWords} <span style={{ color: 'var(--color-primary)' }}>{coloredWord}</span>
                    </h2>
                    <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                        {faqData.subtitle || "Müşterilerimizden en çok gelen sorular ve cevapları"}
                    </p>
                </div>

                <div className="space-y-4">
                    {activeFaqs.map((item, index) => {
                        const isActive = activeAccordion === index;
                        return (
                            <div
                                key={item.id}
                                className="border border-gray-200 rounded-2xl overflow-hidden transition-all duration-300"
                                style={{
                                    borderColor: isActive ? 'var(--color-primary)' : '#e5e7eb',
                                    boxShadow: isActive ? `0 4px 12px color-mix(in srgb, var(--color-primary), transparent 90%)` : 'none'
                                }}
                            >
                                <button
                                    className="flex justify-between items-center w-full p-6 bg-white text-left font-semibold transition-colors"
                                    onClick={() => toggleAccordion(index)}
                                >
                                    <span
                                        className="text-lg transition-colors"
                                        style={{ color: isActive ? 'var(--color-primary)' : '#111827' }}
                                    >
                                        {item.question}
                                    </span>
                                    {isActive ? (
                                        <ChevronUp className="h-5 w-5 shrink-0" style={{ color: 'var(--color-primary)' }} />
                                    ) : (
                                        <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                                    )}
                                </button>

                                {isActive && (
                                    <div
                                        className="p-6 border-t"
                                        style={{
                                            backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 97%)',
                                            borderColor: 'color-mix(in srgb, var(--color-primary), transparent 90%)'
                                        }}
                                    >
                                        <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{item.answer}</p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}